import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Square, Key, MessageSquare, Send, Car } from "lucide-react";
import { Button, Input, Select, Textarea, Card, Badge, Modal, Spinner } from "../shared/ui";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MessagesPage = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [newMessage, setNewMessage] = useState({ recipient_id: "", subject: "", content: "" });
  const [replyContent, setReplyContent] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);

  const canSendMessages = ['admin', 'manager', 'supervisor', 'office_staff'].includes(user?.role);

  useEffect(() => {
    fetchConversations();
    fetchEmployees();
  }, []);

  const fetchConversations = async () => {
    try {
      const [convRes, unreadRes] = await Promise.all([
        axios.get(`${API}/messages/conversations`),
        axios.get(`${API}/messages/unread-count`)
      ]);
      setConversations(convRes.data);
      setUnreadCount(unreadRes.data.unread_count);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setEmployees(res.data.filter(emp => emp.id !== user?.id));
    } catch (e) {}
  };

  const openConversation = async (conversationId) => {
    try {
      const res = await axios.get(`${API}/messages?conversation_id=${conversationId}`);
      setMessages(res.data);
      setSelectedConversation(conversationId);
      // Mark as read
      await axios.put(`${API}/messages/conversation/${conversationId}/read-all`);
      fetchConversations();
    } catch (e) {
      console.error(e);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.recipient_id || !newMessage.content) {
      alert("Please select a recipient and enter a message");
      return;
    }
    try {
      await axios.post(`${API}/messages`, newMessage);
      alert("Message sent successfully");
      setShowNewMessage(false);
      setNewMessage({ recipient_id: "", subject: "", content: "" });
      fetchConversations();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to send message");
    }
  };

  const handleReply = async (lastMessageId) => {
    if (!replyContent.trim()) return;
    try {
      await axios.post(`${API}/messages/${lastMessageId}/reply`, { content: replyContent });
      setReplyContent("");
      openConversation(selectedConversation);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to send reply");
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="messages-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold uppercase tracking-tight font-heading">Messages</h1>
          {unreadCount > 0 && (
            <Badge variant="warning">{unreadCount} unread</Badge>
          )}
        </div>
        {canSendMessages && (
          <Button onClick={() => setShowNewMessage(true)} data-testid="new-message-btn">
            <Plus className="w-5 h-5 mr-2" />
            New Message
          </Button>
        )}
      </div>

      {/* New Message Modal */}
      {showNewMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowNewMessage(false)}>
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">New Message</h3>
            <form onSubmit={handleSendMessage} className="space-y-4">
              <Select
                label="To"
                value={newMessage.recipient_id}
                onChange={(e) => setNewMessage({...newMessage, recipient_id: e.target.value})}
                options={[
                  { value: "", label: "Select recipient..." },
                  ...employees.map(e => ({ value: e.id, label: `${e.first_name} ${e.last_name} (${e.role})` }))
                ]}
              />
              <Input
                label="Subject (optional)"
                value={newMessage.subject}
                onChange={(e) => setNewMessage({...newMessage, subject: e.target.value})}
                placeholder="What's this about?"
              />
              <Textarea
                label="Message"
                value={newMessage.content}
                onChange={(e) => setNewMessage({...newMessage, content: e.target.value})}
                placeholder="Type your message..."
                rows={4}
              />
              <div className="flex gap-3">
                <Button type="submit">
                  <Send className="w-4 h-4 mr-2" />
                  Send
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowNewMessage(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        {/* Conversations List */}
        <div className="md:col-span-1">
          <Card className="overflow-hidden">
            <h3 className="font-bold uppercase text-sm mb-4">Conversations</h3>
            {conversations.length > 0 ? (
              <div className="space-y-1 -mx-4 -mb-4">
                {conversations.map(conv => (
                  <button
                    key={conv.conversation_id}
                    onClick={() => openConversation(conv.conversation_id)}
                    className={`w-full p-4 text-left hover:bg-slate-50 transition-colors border-b border-slate-100 ${
                      selectedConversation === conv.conversation_id ? 'bg-orange-50 border-l-4 border-l-orange-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-semibold">{conv.other_user_name}</p>
                      <span className="text-xs text-slate-400">{formatTime(conv.last_message_at)}</span>
                    </div>
                    <p className="text-sm text-slate-500 truncate">{conv.last_message}</p>
                    {conv.unread_count > 0 && (
                      <Badge variant="warning" className="mt-1">{conv.unread_count} new</Badge>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No conversations yet</p>
              </div>
            )}
          </Card>
        </div>

        {/* Message Thread */}
        <div className="md:col-span-2">
          <Card className="min-h-[400px] flex flex-col">
            {selectedConversation && messages.length > 0 ? (
              <>
                <h3 className="font-bold uppercase text-sm mb-4">
                  Conversation with {conversations.find(c => c.conversation_id === selectedConversation)?.other_user_name}
                </h3>
                
                <div className="flex-1 space-y-4 overflow-y-auto max-h-[400px] mb-4">
                  {messages.slice().reverse().map(msg => (
                    <div 
                      key={msg.id}
                      className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] p-3 rounded-lg ${
                        msg.sender_id === user?.id 
                          ? 'bg-orange-500 text-white' 
                          : 'bg-slate-100 text-slate-800'
                      }`}>
                        {msg.subject && (
                          <p className={`text-xs font-semibold mb-1 ${
                            msg.sender_id === user?.id ? 'text-orange-100' : 'text-slate-500'
                          }`}>
                            {msg.subject}
                          </p>
                        )}
                        <p className="text-sm">{msg.content}</p>
                        <p className={`text-xs mt-1 ${
                          msg.sender_id === user?.id ? 'text-orange-200' : 'text-slate-400'
                        }`}>
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Reply Box */}
                <div className="flex gap-2 pt-4 border-t border-slate-200">
                  <input
                    type="text"
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="Type a reply..."
                    className="flex-1 px-4 py-2 border-2 border-slate-200 rounded-lg focus:border-orange-500 focus:outline-none"
                    onKeyPress={(e) => e.key === 'Enter' && handleReply(messages[0]?.id)}
                  />
                  <Button onClick={() => handleReply(messages[0]?.id)}>
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-500">
                <div className="text-center">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">Select a conversation</p>
                  <p className="text-sm">Or start a new message</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};



export default MessagesPage;
